import {
  faArchive,
  faBackward,
  faCheckCircle,
  faClock,
  faContactCard,
  faDownload,
  faEllipsisV,
  faExclamationCircle,
  faListCheck,
  faPencil,
  faQuestionCircle,
  faTrash,
  type IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useKeycloak } from "@react-keycloak/web";
import { Card } from "@ventumsoftware/react-toolkit";
import { type FC, useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useApi, useFetchedResource } from "../api/APIContext";
import { useApiCall } from "../api/api";
import type { Assessor, Company, Project } from "../api/dto";
import { type Inspection, inspectionStatusLabels } from "../api/dto/Inspection";
import { ContentContainer } from "../components/content/ContentContainer";
import { PageHeader } from "../components/content/PageHeader";
import { Button, IconButton } from "../components/form/Button";
import { Select } from "../components/form/Select";
import { InspectionAttachmentsOverview } from "../components/inspections/InspectionAttachmentsOverview";
import { InspectionPhotosOverview } from "../components/inspections/InspectionPhotosOverview";
import { SharedInspectionFindingsOverview } from "../components/inspections/SharedInspectionFindingsOverview";
import type { EnrichedInspection } from "../components/inspections/types";
import { useEnrichedInspection } from "../components/inspections/useEnrichedInspections";
import { Loading } from "../components/Loading";
import { useModal } from "../components/layout/ModalProvider";
import { useRefresh, useRefreshEffect } from "../components/RefreshController";
import { ArchiveInspectionModal } from "../modals/ArchiveInspectionModal";
import { EditInspectionContactModal } from "../modals/EditInspectionContactModal";
import { EditInspectionDetailsModal } from "../modals/EditInspectionDetailsModal";
import { EditInspectionModal } from "../modals/EditInspectionModal";
import { EditInspectionRemarksModal } from "../modals/EditInspectionRemarksModal";
import { EditProjectContactModal } from "../modals/EditProjectContactModal";
import { PermanentDeleteInspectionModal } from "../modals/PermanentDeleteInspectionModal";
import { RevertInspectionModal } from "../modals/RevertInspectionModal";

export const InspectionDetailsPage: FC = () => {
  const { inspectionId } = useParams();
  const { inspections, projects, companies, assessors, loading, refresh } = useApi();
  // check if customer
  const { keycloak } = useKeycloak();
  const roles = useMemo(
    () => keycloak.tokenParsed?.realm_access?.roles ?? [],
    [keycloak.tokenParsed],
  );
  const isCustomer = roles.includes("customer");

  const enrichedInspection = useEnrichedInspection(inspectionId);

  return (
    <ContentContainer size={"lg"}>
      <Loading loading={loading || !enrichedInspection}>
        {enrichedInspection && (
          <div>
            <div className={"flex justify-between items-center"}>
              <PageHeader>Assessment {enrichedInspection.name ?? ""}</PageHeader>
              {!isCustomer && (
                <div className={"h-12 mb-6 flex items-center"}>
                  <InspectionAssessor
                    inspection={enrichedInspection}
                    assessors={assessors}
                    currentAssessor={
                      enrichedInspection.assessorId ? enrichedInspection.assessorId : "-"
                    }
                  />
                  <InspectionDetailsContextMenu
                    inspection={enrichedInspection}
                    inspections={inspections}
                    projects={projects}
                    companies={companies}
                  />
                </div>
              )}
            </div>
            {enrichedInspection.archivedAt && (
              <div
                role={"status"}
                className={
                  "mb-4 flex items-center gap-3 rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900"
                }
              >
                <FontAwesomeIcon icon={faArchive} />
                <div>
                  <span className={"font-semibold"}>Deze inspectie is gearchiveerd</span>
                  <span> op {enrichedInspection.archivedAt.toLocaleString("nl")}.</span>
                </div>
              </div>
            )}
            <div className={"space-y-5"}>
              <InspectionDetailsOverview inspection={enrichedInspection} isCustomer={isCustomer} />
              <InspectionContactsOverview inspection={enrichedInspection} isCustomer={isCustomer} />
              <InspectionResultsOverview
                inspection={enrichedInspection}
                isCustomer={isCustomer}
                reload={refresh.inspections}
              />
              <InspectionQuestionsLinkCard
                inspection={enrichedInspection}
                isCustomer={isCustomer}
              />
              <SharedInspectionFindingsOverview
                inspection={enrichedInspection}
                isCustomer={isCustomer}
              />
              <InspectionPhotosOverview inspection={enrichedInspection} isCustomer={isCustomer} />
              <InspectionAttachmentsOverview
                inspection={enrichedInspection}
                isCustomer={isCustomer}
              />
            </div>
          </div>
        )}
      </Loading>
    </ContentContainer>
  );
};

const InspectionAssessor: FC<{
  inspection: Inspection;
  assessors: Assessor[];
  currentAssessor: string;
}> = (props) => {
  const { addOrUpdateInspection } = useApiCall();
  const reload = useRefresh();
  const refactoredAssessors = props.assessors.map((item) => [
    item.id,
    item.first_name + " " + item.last_name,
  ]);
  const [selectedAssessor, setSelectedAssessor] = useState<string | null>(props.currentAssessor);

  const saveNewValues = useCallback(async (assessorId: string) => {
    await addOrUpdateInspection(props.inspection.id, {
      assessor_id: assessorId,
    });
    reload();
  }, []);

  return (
    <div>
      <Select
        label={"Inspecteur"}
        inline
        value={selectedAssessor ?? "-"}
        options={Object.fromEntries(refactoredAssessors)}
        onChange={async (key) => {
          setSelectedAssessor(key);
          await saveNewValues(key);
        }}
      />
    </div>
  );
};
const InspectionDetailsOverview: FC<{ inspection: EnrichedInspection; isCustomer: boolean }> = (
  props,
) => {
  const refresh = useRefresh();
  const editInspectionModal = useModal({
    title: "Inspection bewerken",
    size: "xl",
    body: <EditInspectionModal inspection={props.inspection} />,
  });
  const editInspectionDetailsModal = useModal({
    title: "Inspectie instellen",
    size: "xl",
    body: <EditInspectionDetailsModal inspection={props.inspection} onFinish={() => refresh()} />,
  });

  if (props.isCustomer) {
    return <></>;
  }
  return (
    <div className={"grid grid-cols-1 xl:grid-cols-2 gap-4"}>
      <div>
        <Card title={"Inspectieinstellingen"}>
          <table>
            <tbody>
              <tr>
                <td className={"font-medium pr-3 py-1"}>Job Number:</td>
                <td className={"py-2"}>{props.inspection.inspectionNumber || "-"}</td>
              </tr>
              <tr>
                <td className={"font-medium pr-3 py-1 align-top"}>Hijsfirma:</td>
                <td className={"py-2"}>
                  {(props.inspection.extraFieldsFormatted["Hijsfirma"] ?? "-")
                    .split(",")
                    .map((f) => f.trim())
                    .filter((f) => f !== "")
                    .map((firm, i) => (
                      <div key={i}>{firm}</div>
                    ))}
                </td>
              </tr>
              <tr>
                <td className={"font-medium pr-3 py-1"}>Weercondities:</td>
                <td className={"py-2"}>{props.inspection.extraFieldsFormatted["Weer"] ?? "-"}</td>
              </tr>
              {props.inspection.loadCapacitiesFormatted.length > 0 ? (
                props.inspection.loadCapacitiesFormatted.map((load_sentence, index) => {
                  return (
                    <tr key={index}>
                      <td className={"font-medium pr-3 py-1 min-w-36"}>
                        Hijslast
                        {props.inspection.loadCapacitiesFormatted.length >= 2 ? (
                          <span> {index + 1}</span>
                        ) : (
                          ""
                        )}
                        :
                      </td>
                      <td className={"py-2"}>{load_sentence}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td className={"font-medium pr-3 py-1"}>Hijslast:</td>
                  <td className={"py-2"}>{"-"}</td>
                </tr>
              )}
              {props.inspection.liftingEquipmentsFormatted.length > 0 ? (
                props.inspection.liftingEquipmentsFormatted.map((lifting_equipment, index) => {
                  return (
                    <tr key={index}>
                      <td className={"font-medium pr-3 py-1"}>
                        Inzet materieel
                        {props.inspection.liftingEquipmentsFormatted.length >= 2 ? (
                          <span> {index + 1}</span>
                        ) : (
                          ""
                        )}
                        :
                      </td>
                      <td className={"py-2"}>{lifting_equipment}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td className={"font-medium pr-3 py-1"}>Inzet materieel:</td>
                  <td className={"py-2"}>{"-"}</td>
                </tr>
              )}
            </tbody>
          </table>
          <hr className={"-mx-6 my-3"} />
          <Button
            type={"secondary"}
            size={"md"}
            icon={faPencil}
            text={`Bewerken`}
            onClick={() => editInspectionDetailsModal.open()}
          />
        </Card>
      </div>
      <div>
        <Card title={"Basisgegevens"}>
          <table>
            <tbody>
              <tr>
                <td className={"font-medium pr-3 py-1"}>Inspectie naam:</td>
                <td>{props.inspection.name || "-"}</td>
              </tr>
              <tr>
                <td className={"font-medium pr-3 py-1"}>Bedrijf:</td>
                <td>{props.inspection.company.name ?? "-"}</td>
              </tr>
              <tr>
                <td className={"font-medium pr-3 py-1"}>Project:</td>
                <td>{props.inspection.project.name ?? "-"}</td>
              </tr>
              <tr>
                <td className={"font-medium pr-3 py-1"}>Vragenlijst:</td>
                <td>{props.inspection.questionSet.name ?? "-"}</td>
              </tr>
            </tbody>
          </table>
          <hr className={"-mx-6 my-3"} />
          <Button
            type={"secondary"}
            size={"md"}
            icon={faPencil}
            text={`Bewerken`}
            onClick={() => editInspectionModal.open()}
          />
        </Card>
      </div>
    </div>
  );
};

const InspectionContactsOverview: FC<{ inspection: EnrichedInspection; isCustomer: boolean }> = (
  props,
) => {
  const editProjectContactModal = useModal({
    title: "Contactpersoon instellen",
    body: <EditProjectContactModal project={props.inspection.project} />,
  });
  const editInspectionContactModal = useModal({
    title: "Contactpersoon instellen",
    body: <EditInspectionContactModal inspection={props.inspection} />,
  });

  if (props.isCustomer) {
    return <></>;
  }
  return (
    <div className={"grid grid-cols-1 xl:grid-cols-2 gap-4"}>
      <Card title={"Contactpersoon vanuit Project"}>
        <table>
          <tbody>
            <tr>
              <td className={"font-medium pr-3 py-1"}>Naam:</td>
              <td>{props.inspection.project.contactName ?? "-"}</td>
            </tr>
            <tr>
              <td className={"font-medium pr-3 py-1"}>E-mail:</td>
              <td>{props.inspection.project.contactEmail ?? "-"}</td>
            </tr>
            <tr>
              <td className={"font-medium pr-3 py-1"}>Phone:</td>
              <td>{props.inspection.project.contactPhone ?? "-"}</td>
            </tr>
            <tr>
              <td className={"font-medium pr-3 py-1"}>Extra:</td>
              <td>{props.inspection.project.contactExtra ?? "-"}</td>
            </tr>
          </tbody>
        </table>
        <hr className={"-mx-6 my-3"} />
        <Button
          type={"secondary"}
          size={"md"}
          icon={faContactCard}
          text={`Contactpersoon aanpassen voor ${props.inspection.project.name}`}
          onClick={() => editProjectContactModal.open()}
        />
      </Card>
      <Card title={"Contactpersoon op Inspectie"}>
        <table>
          <tbody>
            <tr>
              <td className={"font-medium pr-3 py-1"}>Naam:</td>
              <td>{props.inspection.contactName ?? "-"}</td>
            </tr>
            <tr>
              <td className={"font-medium pr-3 py-1"}>E-mail:</td>
              <td>{props.inspection.contactEmail ?? "-"}</td>
            </tr>
            <tr>
              <td className={"font-medium pr-3 py-1"}>Phone:</td>
              <td>{props.inspection.contactPhone ?? "-"}</td>
            </tr>
            <tr>
              <td className={"font-medium pr-3 py-1"}>Extra:</td>
              <td>{props.inspection.contactExtra ?? "-"}</td>
            </tr>
          </tbody>
        </table>
        <hr className={"-mx-6 my-3"} />
        <Button
          type={"secondary"}
          size={"md"}
          icon={faContactCard}
          text={`Contactpersoon aanpassen voor Inspectie`}
          onClick={() => editInspectionContactModal.open()}
        />
      </Card>
    </div>
  );
};

const InspectionQuestionsLinkCard: FC<{ inspection: EnrichedInspection; isCustomer: boolean }> = (
  props,
) => {
  const navigate = useNavigate();

  if (props.isCustomer) {
    return null;
  }
  return (
    <Card title={"Vragen"}>
      <div className={"flex items-center justify-between"}>
        <p className={"text-slate-800"}>
          Bekijk en bewerk de vragenlijst en registreer bevindingen.
        </p>
        <Button
          type={"primary"}
          size={"md"}
          icon={faListCheck}
          text={"Vragen bekijken"}
          onClick={() => navigate(`/inspections/${props.inspection.id}/questions`)}
        />
      </div>
    </Card>
  );
};

const InspectionResultsOverview: FC<{
  inspection: EnrichedInspection;
  isCustomer: boolean;
  reload: (silently: boolean) => Promise<void>;
}> = (props) => {
  const { getInspectionReportUrl, updateInspectionStatus } = useApiCall();
  const [override, setOverride] = useState(false);
  // props.inspection.status is "Gearchiveerd" for archived inspections, so derive the progress of the inspection itself.
  const progress =
    props.inspection.finishedAt !== null
      ? "Afgerond"
      : props.inspection.startedAt === null
        ? "Concept"
        : "In uitvoering";
  const [quality, setQuality] = useState<string>("high_quality");

  const reportStyle = {
    draft: "text-amber-800 bg-yellow-100",
    definitive: "text-brand-900 bg-brand-100",
    none: "text-red-800 bg-red-100",
  }[props.inspection.inspectionStatus ?? "none"];

  const setStatusToDraft = async () => {
    await updateInspectionStatus(
      props.inspection.id,
      Object.keys(inspectionStatusLabels).find(
        (k) =>
          inspectionStatusLabels[k as keyof typeof inspectionStatusLabels] ===
          inspectionStatusLabels.draft,
      )!,
    );
    props.reload(false);
  };

  const setStatusToDefinite = async () => {
    await updateInspectionStatus(
      props.inspection.id,
      Object.keys(inspectionStatusLabels).find(
        (k) =>
          inspectionStatusLabels[k as keyof typeof inspectionStatusLabels] ===
          inspectionStatusLabels.definitive,
      )!,
    );
    props.reload(false);
  };

  return (
    <div>
      <Card title={"Resultaten"}>
        {(progress === "Afgerond" || override) && (
          <div className={"flex flex-col space-y-4"}>
            <div className={"flex flex-row space-x-4"}>
              <div className={"flex flex-col flex-1"}>
                <h3 className={"font-bold"}>
                  Overall Test Results / Field Inspection Report [.PDF]
                </h3>
                <p className={"text-slate-800"}>De uitlslagen in een .pdf bestand</p>
              </div>

              <div className={"flex items-center space-x-4"}>
                <Select
                  label={"Kwaliteit"}
                  inline
                  options={{
                    original: "Origineel",
                    high_quality: "Hoge kwaliteit",
                    compact: "Compact",
                  }}
                  value={quality}
                  onChange={setQuality}
                />
                <Button
                  size={"md"}
                  type={"primary"}
                  icon={faDownload}
                  text={"Download"}
                  onClick={() => {
                    window.open(getInspectionReportUrl(props.inspection.id, quality), "_blank");
                    // window.open(getInspectionExportUrl(props.inspection.id, true), '_blank')
                  }}
                />
              </div>
            </div>

            <hr className={"-mx-6"} />

            {/* Inspectie status */}
            <div className={"flex flex-col space-y-4"}>
              <div className={"flex"}>
                <div className={"flex flex-col flex-1"}>
                  <h3 className={"font-bold"}>Inspectie Status</h3>
                  <p>
                    De status van de inspectie, dat beschrijft wat de huidige status van de
                    inspectie is
                  </p>
                </div>
                <div className={"flex justify-center items-center"}>
                  <div
                    className={`flex flex-row items-center space-x-2 bg-brand-100 px-2 py-0.5 rounded-full`}
                  >
                    <FontAwesomeIcon icon={faCheckCircle} className={"text-brand-900"} />
                    <h2 className={"font-lg font-semibold text-brand-800"}>Finished</h2>
                  </div>
                </div>
              </div>

              <hr className={"-mx-6"} />

              {/* Rapport Status */}
              <div className={"flex"}>
                <div className={"flex flex-col flex-1"}>
                  <h3 className={"font-bold"}>Rapport Status</h3>
                  <p>De status van het rapport dat bij de inspectie hoort. </p>
                  <p>
                    'Voorlopig' = De inspectie is uitgevoerd, maar nog niet alle bevindingen zijn
                    genoteerd
                  </p>
                  <p>
                    'Definitief' = Alle bevindingen / vervolg acties vanuit de inspectie zijn bekend
                    en/of uitgevoerd{" "}
                  </p>
                </div>
                <div className={"flex flex-col justify-center items-center space-y-2"}>
                  <div
                    className={`flex flex-row items-center space-x-2 ${reportStyle} px-2 py-0.5 rounded-full`}
                  >
                    <FontAwesomeIcon
                      icon={
                        props.inspection.inspectionStatus === "definitive"
                          ? faCheckCircle
                          : props.inspection.inspectionStatus === "draft"
                            ? faExclamationCircle
                            : faQuestionCircle
                      }
                    />
                    <h2 className={`font-lg font-semibold`}>
                      {props.inspection.inspectionStatus === null
                        ? "Geen status"
                        : inspectionStatusLabels[props.inspection.inspectionStatus]}
                    </h2>
                  </div>

                  {props.inspection.inspectionStatus === "definitive" ? (
                    <Button
                      type={"secondary"}
                      size={"sm"}
                      text={"Zet naar voorlopig"}
                      onClick={setStatusToDraft}
                    />
                  ) : (
                    <Button
                      type={"secondary"}
                      size={"sm"}
                      text={"Zet naar definitief"}
                      onClick={setStatusToDefinite}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {progress === "In uitvoering" && !override && (
          <div className={"flex flex-col items-center space-y-3 py-4"}>
            <FontAwesomeIcon icon={faClock} className={"text-brand-900"} />
            <h2 className={"font-lg font-semibold text-brand-800"}>In progress</h2>
            <p className={"text-sm text-brand-700"}>
              Dit inspection is nog niet afgerond, pas dan kun je de resultaten exporteren
            </p>
            <button className={"text-sm underline text-red-700"} onClick={() => setOverride(true)}>
              Toch nu exporteren
            </button>
          </div>
        )}
        {progress === "Concept" && !override && (
          <div className={"flex flex-col items-center space-y-3 py-4"}>
            <FontAwesomeIcon icon={faPencil} className={"text-brand-900"} />
            <h2 className={"font-lg font-semibold text-brand-800"}>Concept</h2>
            <p className={"text-sm text-brand-700"}>
              Dit inspection is nog niet afgerond, pas dan kun je de resultaten exporteren
            </p>
            <button className={"text-sm underline text-red-700"} onClick={() => setOverride(true)}>
              Toch nu exporteren
            </button>
          </div>
        )}
        <InspectionRemarksOverview inspection={props.inspection} />
      </Card>
    </div>
  );
};

const InspectionRemarksOverview: FC<{ inspection: EnrichedInspection }> = (props) => {
  const { getInspectionRemarks } = useApiCall();
  const remarksResource = useFetchedResource((inspectionId: string) =>
    getInspectionRemarks(inspectionId),
  );
  useRefreshEffect(() => {
    remarksResource.reload(props.inspection.id);
  });

  const editRemarksModal = useModal({
    title: "Algemene opmerkingen bewerken",
    size: "xl",
    body: (
      <EditInspectionRemarksModal
        inspection={props.inspection}
        remarks={remarksResource.resource ?? null}
        onFinished={() => remarksResource.reload(props.inspection.id, true)}
      />
    ),
  });

  if (!remarksResource.loading && remarksResource.resource === undefined) {
    return <></>;
  }

  return (
    <div className={"flex flex-col space-y-4 mt-4"}>
      <hr className={"-mx-6"} />
      <div className={"flex"}>
        <div className={"flex flex-col flex-1"}>
          <h3 className={"font-bold"}>Algemene opmerkingen</h3>
          <p className={"whitespace-pre-line"}>
            {remarksResource.resource || "Geen opmerkingen ingevuld"}
          </p>
        </div>
        <div className={"flex items-center"}>
          <Button
            type={"secondary"}
            size={"sm"}
            icon={faPencil}
            text={"Bewerken"}
            onClick={() => editRemarksModal.open()}
          />
        </div>
      </div>
    </div>
  );
};

const InspectionDetailsContextMenu: FC<{
  inspection: EnrichedInspection;
  companies: Company[];
  projects: Project[];
  inspections: Inspection[];
}> = (props) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const { keycloak } = useKeycloak();
  const roles = useMemo(
    () => keycloak.tokenParsed?.realm_access?.roles ?? [],
    [keycloak.tokenParsed],
  );
  const editInspectionModal = useModal({
    title: "Inspection bewerken",
    size: "xl",
    body: <EditInspectionModal inspection={props.inspection} />,
  });
  const archiveInspectionModal = useModal({
    title: "Inspectie archiveren",
    body: <ArchiveInspectionModal inspection={props.inspection} />,
  });
  const revertInspectionModal = useModal({
    title: "Inspectie terugzetten",
    body: <RevertInspectionModal inspection={props.inspection} />,
  });
  const permanentDeleteInspectionModal = useModal({
    title: "Inspectie permanent verwijderen",
    body: <PermanentDeleteInspectionModal inspection={props.inspection} />,
  });
  const options = (
    <>
      {props.inspection.archivedAt ? (
        <>
          <InspectionDetailsContextMenuOption
            text={"Inspectie terugzetten"}
            icon={faBackward}
            onClick={() => revertInspectionModal.open()}
          />
          {roles.includes("admin") && (
            <InspectionDetailsContextMenuOption
              danger
              text={"Inspectie permanent verwijderen"}
              icon={faTrash}
              onClick={() => permanentDeleteInspectionModal.open()}
            />
          )}
        </>
      ) : (
        <>
          {roles.includes("admin") && (
            <InspectionDetailsContextMenuOption
              text={"Inspection bewerken"}
              icon={faPencil}
              onClick={() => editInspectionModal.open()}
            />
          )}
          <InspectionDetailsContextMenuOption
            danger
            text={"Inspectie archiveren"}
            icon={faArchive}
            onClick={() => archiveInspectionModal.open()}
          />
        </>
      )}
    </>
  );

  return (
    <div className={"ml-3 mt-1 relative"}>
      <IconButton
        size={"md"}
        type={"primary"}
        icon={faEllipsisV}
        onClick={() => setExpanded(true)}
      />
      {expanded && (
        <>
          <div className={"fixed inset-0 bg-[#0001]"} onClick={() => setExpanded(false)}></div>
          <div
            className={
              "absolute top-10 right-0 min-w-48 bg-white rounded-sm shadow-lg border border-slate-300"
            }
          >
            {options}
          </div>
        </>
      )}
    </div>
  );
};
const InspectionDetailsContextMenuOption: FC<{
  text: string;
  icon: IconDefinition;
  danger?: true;
  onClick: () => Promise<void> | void;
}> = (props) => {
  return (
    <button
      className={`flex w-full items-center h-10 px-2 ${props.danger ? "text-red-600 hover:bg-red-50" : "text-brand-900 hover:bg-brand-50"}`}
      onClick={props.onClick}
    >
      <div className={"w-10 h-10 flex items-center justify-center -ml-2"}>
        <FontAwesomeIcon icon={props.icon} className={""} />
      </div>
      <span className={"whitespace-nowrap"}>{props.text}</span>
    </button>
  );
};
